import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConsoleTopbar } from './ConsoleShell';
import { MobileHeader } from './MobileHeader';
import { Icon } from './Icon';
import { useIsDesktop } from '@/hooks/useIsDesktop';

/**
 * Page wrapper for console screens. Renders the desktop sticky topbar or the
 * mobile header depending on device, then a padded body. Keeps each
 * instructor/admin screen free of chrome boilerplate.
 *
 * Pass `back` (a route string or a callback) on a drill-in / sub page to surface
 * a back affordance — a chevron in the mobile header and a "Back" link on desktop.
 */
export function ConsolePage({
  title,
  th,
  crumbs,
  actions,
  maxWidth = 1100,
  back,
  children,
}: {
  title: string;
  th?: string;
  crumbs?: string;
  actions?: ReactNode;
  maxWidth?: number;
  back?: string | (() => void);
  children: ReactNode;
}) {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const goBack = back === undefined ? undefined : typeof back === 'function' ? back : () => navigate(back);
  return (
    <>
      {isDesktop ? (
        <ConsoleTopbar title={title} th={th} crumbs={crumbs} actions={actions} />
      ) : (
        // Surface the page action (e.g. "New invite") in the mobile header —
        // previously dropped, so the button was unreachable once a list had rows.
        <MobileHeader title={title} th={th} right={actions} onBack={goBack} />
      )}
      <div style={{ padding: isDesktop ? 28 : '16px 18px 28px' }}>
        <div style={{ maxWidth, margin: '0 auto' }}>
          {goBack && isDesktop && (
            <button className="btn btn-ghost btn-sm" onClick={goBack} style={{ marginBottom: 16, marginLeft: -6 }}>
              <Icon name="chevL" size={15} /> Back
            </button>
          )}
          {children}
        </div>
      </div>
    </>
  );
}
